<script setup lang="ts">
import { uniqBy } from 'lodash'
import { $lineItemZoneList } from '@/api/brandings'

interface IProps {
  modelValue?: any[],
  label?: string,
  selectOptions?: any[] | null,
  /**
   * Optional array of itemZone IDs that should be disabled for selection.
   * If null or undefined, no itemZones will be disabled.
   */
  disabledItemZones?: number[] | null,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => ([]),
  label: 'Zone',
  selectOptions: null,
  disabledItemZones: null
})

interface CustomEvents {
  'update:modelValue': (newValue: any) => void;
}

const emit = defineEmits<CustomEvents>()

const itemZones = ref<string[]>([])
const itemZonesCurrentPage = ref<number>(1)
const itemZonesPerPage: number = 10
const itemZonesSearchStr = ref<string>('')
const isNextItemZonesPageExist = ref<boolean>(false)
const itemZonesLoading = ref<boolean>(false)

const itemZoneOptions = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return itemZones.value
  }
})

const getItemZones = async () => {
  itemZonesLoading.value = true
  try {
    const res = await $lineItemZoneList({
      search: itemZonesSearchStr.value,
      per_page: itemZonesPerPage,
      page: itemZonesCurrentPage.value
    })

    // Append the new itemZones to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    itemZones.value = uniqBy([...itemZones.value, ...res.data.results], 'id')
    isNextItemZonesPageExist.value = res.data.next
  } catch (e) {
    console.log(e)
  } finally {
    itemZonesLoading.value = false
  }
}

const onFocusItemZoneSelect = async () => {
  if (!itemZones.value.length && !props.selectOptions) {
    itemZones.value = []
    itemZonesCurrentPage.value = 1
    await getItemZones()
  }
}

const onLoadMoreItemZones = async () => {
  itemZonesCurrentPage.value++ // Increment the current page to load the next page of itemZones
  await getItemZones()
}

const clearAndGetItemZones = (searchStr: string) => {
  itemZones.value = []
  itemZonesCurrentPage.value = 1 // Set current page => 1 to load initial first page of itemZones
  itemZonesSearchStr.value = searchStr
  getItemZones()
}

const handleItemZoneSearch = useDebounceFn((searchStr: string) => {
  if (!props.selectOptions) {
    clearAndGetItemZones(searchStr)
  }
}, 500)

defineExpose({ clearAndGetItemZones })
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...itemZoneOptions]"
    :label="label"
    density="compact"
    :no-filter="!selectOptions"
    multiple
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusItemZoneSelect"
    @update:search="handleItemZoneSearch"
  >
    <template
      v-if="disabledItemZones"
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :disabled="!!disabledItemZones ? disabledItemZones.includes(item?.value) : false"
      />
    </template>
    <template #selection="{ item, index }">
      <div class="d-flex align-center">
        <v-chip v-if="index < 1">
          <span>{{ item.title }}</span>
        </v-chip>
        <span
          v-if="index === 1"
          class="text-grey text-caption align-self-center"
        >
          (+{{ modelValue?.length - 1 }} others)
        </span>
      </div>
    </template>
    <template #no-data>
      <div
        v-if="!!itemZonesLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Zones not found
      </div>
    </template>
    <template
      v-if="isNextItemZonesPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!itemZonesLoading"
        @click="onLoadMoreItemZones"
      >
        Load more...
        <VProgressCircular
          v-if="!!itemZonesLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>

<style lang="scss">

</style>
